/**
 * GpuVolumeStrategy — the GPU volume-actor RenderStrategy. RELOCATES the real
 * Cornerstone calls that previously lived inline in `VolumeViewport.tsx`
 * (enableElement ORTHOGRAPHIC + setVolumesForViewports + jumpToWorld/jumpToSlice
 * + setBlendMode/setSlabThickness + camera zoom/pan) into a small, stateless
 * class behind the `RenderStrategy` interface (design spec §9.6).
 *
 * Stateless except: the injected deps, the current `volumeId` (needed by
 * showIndex's jumpToSlice fallback), and the current blend mode + slab (so a
 * `setBlend` call before `attach` is applied on attach). Everything else is
 * read live from the Cornerstone viewport.
 *
 * This strategy does NOT own engine lifecycle, tools, atoms, segmentation, or
 * volume caching — those belong to the Workspace / Session / NavigationController.
 */
import type { RenderingEngine, VolumeViewport } from "@cornerstonejs/core";
import { getCornerstone, getEnums } from "../../utils/dicomLoader";
import { logger } from "../../utils/logger";
import type {
  BlendMode,
  CameraState,
  PlaneOrientation,
  RenderStrategyKind,
  WorldPos,
} from "../types";
import type { RenderStrategy } from "./RenderStrategy";
import { snapViewportCameraToCardinal } from "../cameraSnap";
import { applyRadiologicalFlip } from "../radiologicalOrientation";
import { resolveOrientationAxis } from "./orientationAxis";

export interface GpuVolumeStrategyDeps {
  viewportId: string;
  /** Returns the shared rendering engine. In standalone use, pass getOrCreateSharedRenderingEngine. */
  getEngine: () => RenderingEngine;
  /**
   * Optional gate for the post-attach cardinal camera snap. Evaluated at attach
   * time; defaults to snapping. The labeling segmentation surface passes a
   * predicate that returns false so the camera stays on the series' true
   * (possibly oblique) orientation — otherwise IPM-placed contours render across
   * multiple slices (the coronal mask doubling). See cameraSnap.ts.
   */
  shouldSnapCamera?: () => boolean;
  /**
   * Optional gate for the post-attach radiological-L/R camera flip. Evaluated
   * at attach time, so it re-applies on context-loss recovery (recover()
   * re-runs attach). The mount owns the decision — it is the only place that
   * knows both the series' stored IOP and whether this pane is an MPR reslice
   * — so the strategy never re-derives it from a second source. See
   * paneNeedsRadiologicalFlip in radiologicalOrientation.ts.
   */
  shouldFlipHorizontal?: () => boolean;
  /**
   * Reslice orientation for MPR panes. Undefined → ACQUISITION (the series'
   * native plane), the default for every non-reslice pane. "Coronal"/"Sagittal"
   * reslice the shared volume; see resolveGridReslice.ts / the MPR-reslice spec.
   */
  orientation?: PlaneOrientation;
}

export class GpuVolumeStrategy implements RenderStrategy {
  readonly kind: RenderStrategyKind = "GpuVolume";

  private readonly deps: GpuVolumeStrategyDeps;
  /** Set on attach; reused by showIndex's jumpToSlice volumeId fallback. */
  private volumeId: string | null = null;
  /** Render-state: remembered so a setBlend before attach lands on attach. */
  private blendMode: BlendMode = "composite";
  private slabThicknessMm: number | undefined = undefined;
  /** The attached element — jumpToSlice takes the HTMLDivElement, not the viewport. */
  private element: HTMLDivElement | null = null;

  constructor(deps: GpuVolumeStrategyDeps) {
    this.deps = deps;
  }

  /**
   * Returns the live Cornerstone viewport for this strategy's id, narrowed to
   * the concrete VolumeViewport type. engine.getViewport() statically returns
   * the base IViewport (no jumpToWorld/setBlendMode/setSlabThickness), but an
   * ORTHOGRAPHIC viewport is a VolumeViewport at runtime — same cast the source
   * used. Returns null if no viewport is enabled yet.
   */
  private getViewport(): VolumeViewport | null {
    const engine = this.deps.getEngine();
    const vp = engine.getViewport(this.deps.viewportId);
    return (vp as unknown as VolumeViewport) ?? null;
  }

  async attach(element: HTMLDivElement, volumeId: string): Promise<void> {
    const cornerstone = getCornerstone();
    const Enums = getEnums();
    const engine = this.deps.getEngine();

    this.element = element;
    this.volumeId = volumeId;

    // Idempotent enableElement so a re-attach (StrictMode double-mount, sibling
    // remount during a swap) doesn't trip "viewport already exists".
    if (!engine.getViewport(this.deps.viewportId)) {
      engine.enableElement({
        viewportId: this.deps.viewportId,
        type: Enums.ViewportType.ORTHOGRAPHIC,
        element,
        defaultOptions: {
          orientation: resolveOrientationAxis(
            this.deps.orientation,
            Enums.OrientationAxis,
          ) as never,
          background: [0, 0, 0] as [number, number, number],
        },
      });
    }

    // ASSUME the volume is already cached — the Session owns createAndCacheVolume.
    await cornerstone.setVolumesForViewports(engine, [{ volumeId }], [this.deps.viewportId]);

    // Apply current blend now that the viewport owns a volume actor.
    this.applyBlend();

    // Snap camera to nearest cardinal axes so sibling MPR reference lines render
    // straight on slightly-oblique series (gantry tilt). Matches the production
    // VolumeViewport path; deliberate obliques (> ~26°) are left alone. Without
    // this, OrientationAxis.ACQUISITION leaves the whole viewport rolled.
    // Skipped on the segmentation surface (shouldSnapCamera → false): snapping
    // an oblique companion off its true orientation makes IPM-placed contours
    // span multiple slices (the coronal mask doubling). Defaults to snapping.
    if (this.deps.shouldSnapCamera ? this.deps.shouldSnapCamera() : true) {
      snapViewportCameraToCardinal(this.getViewport());
    }

    // #coronal-lr: force radiological L/R (patient-left on the viewer's right)
    // for a series stored in neurological convention, AFTER the cardinal snap.
    // Idempotent, so a re-attach (StrictMode, low-res→full swap, context-loss
    // recovery) re-asserts it rather than double-flipping.
    applyRadiologicalFlip(
      this.getViewport(),
      this.deps.shouldFlipHorizontal?.() ?? false,
      `GpuVolumeStrategy ${this.deps.viewportId}`,
    );
  }

  showWorld(world: WorldPos, _orientation: PlaneOrientation): void {
    // The ORTHOGRAPHIC viewport's orientation is fixed at ACQUISITION, so the
    // orientation arg is accepted but NOT used to reorient on the GPU path.
    // Cpu2d uses it to choose the reslice plane.
    const vp = this.getViewport();
    if (!vp || typeof vp.jumpToWorld !== "function") return;
    vp.jumpToWorld([world[0], world[1], world[2]]);
  }

  showIndex(listIndex: number): void {
    // Index-based fallback used when canonical IPP is unavailable for a slice.
    const cornerstone = getCornerstone();
    if (!this.element) return;
    // utilities.jumpToSlice is async; sync try/catch alone leaks the rejection
    // as `Uncaught (in promise)`. Mirror the source: attach `.catch` on the
    // returned promise so rejections route through logger.warn. jumpToSlice's
    // first arg is the HTMLDivElement (not the viewport) — see core's
    // utilities/jumpToSlice.d.ts.
    const result = cornerstone.utilities.jumpToSlice(this.element, {
      imageIndex: listIndex,
      volumeId: this.volumeId ?? undefined,
    });
    if (result && typeof (result as PromiseLike<void>).then === "function") {
      Promise.resolve(result).catch((err: unknown) => {
        logger.warn("[GpuVolumeStrategy] jumpToSlice failed:", err);
      });
    }
  }

  getLandedWorld(): WorldPos | null {
    // Pure read — no writes. The current focal point IS the landed world pos.
    const vp = this.getViewport();
    const cam = vp?.getCamera?.();
    return cam?.focalPoint ? [cam.focalPoint[0], cam.focalPoint[1], cam.focalPoint[2]] : null;
  }

  getCamera(): CameraState {
    const vp = this.getViewport();
    if (!vp) return { zoom: 1, pan: [0, 0] };
    const zoom = typeof vp.getZoom === "function" ? vp.getZoom() : 1;
    const panRaw = typeof vp.getPan === "function" ? vp.getPan() : [0, 0];
    // getPan returns Point2 ([number, number]); guard for viewport types that
    // don't expose it, matching the source's onCameraModified handler.
    const pan: [number, number] = Array.isArray(panRaw) ? [panRaw[0] ?? 0, panRaw[1] ?? 0] : [0, 0];
    return { zoom, pan };
  }

  setCamera({ zoom, pan }: CameraState): void {
    const vp = this.getViewport();
    if (!vp) return;
    if (typeof vp.setZoom === "function") vp.setZoom(zoom);
    if (typeof vp.setPan === "function") vp.setPan([pan[0], pan[1]]);
    vp.render?.();
  }

  setBlend(mode: BlendMode, slabThicknessMm?: number): void {
    // Store as render-state so a setBlend before attach is applied on attach.
    this.blendMode = mode;
    this.slabThicknessMm = slabThicknessMm;
    // If a viewport already exists, apply now.
    this.applyBlend();
  }

  /**
   * Apply the current blend mode + slab to the live viewport. No-op for the
   * default "composite" blend (single-slice behavior). MIP/MinIP map to the
   * BlendModes enum + setSlabThickness — same logic as the source applyBlendMode.
   */
  private applyBlend(): void {
    // Composite is the viewport default; revert is intentionally a no-op here to match the source. If MIP-toggle-off needs an explicit reset, call setBlendMode(COMPOSITE_BLEND) — deferred to Phase 2 (zero-behavior-change relocation).
    if (this.blendMode === "composite") return;
    const vp = this.getViewport();
    if (!vp || typeof vp.setBlendMode !== "function") return;
    try {
      const Enums = getEnums();
      const modeEnum =
        this.blendMode === "MIP"
          ? Enums.BlendModes.MAXIMUM_INTENSITY_BLEND
          : Enums.BlendModes.MINIMUM_INTENSITY_BLEND;
      vp.setBlendMode(modeEnum);
      if (typeof this.slabThicknessMm === "number" && this.slabThicknessMm > 0) {
        vp.setSlabThickness(this.slabThicknessMm);
      }
      vp.render?.();
    } catch (e) {
      logger.error("[GpuVolumeStrategy] applyBlend failed:", e);
    }
  }

  dispose(): void {
    // The strategy registers no listeners of its own (the Workspace /
    // NavigationController own VOLUME_NEW_IMAGE etc.), so there is nothing to
    // remove here. Disable only THIS viewport — NOT disableSharedViewport and
    // NEVER engine.destroy(): the Workspace owns engine lifecycle, and
    // destroying it would orphan sibling panes (the bug we're killing).
    try {
      const engine = this.deps.getEngine();
      engine.disableElement(this.deps.viewportId);
    } catch (err) {
      logger.debug("[GpuVolumeStrategy] dispose disableElement failed:", err);
    } finally {
      this.element = null;
      this.volumeId = null;
    }
  }
}
